import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {

  Thermometer, AlertTriangle, Loader2, ArrowRight, Info,
  MapPin, Search, ChevronDown, ChevronUp, Send, Shield, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { SEO } from '../components/SEO';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, AreaChart, Area
} from 'recharts';
import { getApiBase } from '../lib/apiBase';

const API_URL = getApiBase();

const CEMENT_TYPES = [
  { value: 'CEM_I', label: 'CEM I (Portland)', minAdd: 0, maxAdd: 0 },
  { value: 'CEM_I_FA', label: 'CEM I + Fly Ash', minAdd: 15, maxAdd: 55 },
  { value: 'CEM_I_GGBS', label: 'CEM I + GGBS', minAdd: 25, maxAdd: 75 },
  { value: 'OTHER', label: 'Other (CEM II/A-L, CEM VI, LC3, etc.)', minAdd: 0, maxAdd: 0 },
];

const INSULATION_OPTIONS = [
  { value: 'none', label: 'No insulation (exposed)' },
  { value: 'steel_25mm', label: 'Up to 25mm steel formwork' },
  { value: 'plywood_18mm', label: '18mm plywood' },
  { value: 'plywood_37mm', label: '37mm plywood' },
  { value: 'frost_blanket_10mm', label: '10mm frost blanket' },
  { value: 'frost_blanket_20mm', label: '20mm frost blanket' },
  { value: 'wood_wool_50mm', label: '50mm wood wool' },
  { value: 'expanded_polystyrene', label: 'Expanded polystyrene' },
];

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ─── City Search Component ────────────────────────────────────────────
function CitySearch({ onSelect, selected }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback((q) => {
    if (q.length < 2) { setResults([]); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=8&language=en`);
        const data = await res.json();
        setResults(data.results || []);
        setOpen(true);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          data-testid="city-search-input"
          placeholder="Search for a city..."
          value={selected ? `${selected.name}, ${selected.country}` : query}
          onChange={(e) => {
            if (selected) onSelect(null);
            setQuery(e.target.value);
            search(e.target.value);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
          className="pl-10 pr-10 border-gray-200 text-sm"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
        {selected && (
          <button onClick={() => { onSelect(null); setQuery(''); }} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.map((r, i) => (
            <button
              key={i}
              data-testid={`city-result-${i}`}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-50 last:border-0"
              onClick={() => {
                onSelect({ name: r.name, country: r.country || '', lat: r.latitude, lon: r.longitude, admin1: r.admin1 || '' });
                setQuery('');
                setOpen(false);
              }}
            >
              <span className="font-medium text-gray-800">{r.name}</span>
              {r.admin1 && <span className="text-gray-400">, {r.admin1}</span>}
              <span className="text-gray-400"> — {r.country}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Month Selector ───────────────────────────────────────────────────
function MonthSelector({ value, onChange }) {
  return (
    <div className="grid grid-cols-6 gap-1">
      {MONTH_LABELS.map((m, i) => (
        <button
          key={m}
          data-testid={`month-${i+1}`}
          type="button"
          onClick={() => onChange(i + 1)}
          className={`py-1.5 text-xs font-medium rounded transition-colors ${
            value === i + 1
              ? 'bg-emerald-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >{m}</button>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────
export const ThermalCalculator = () => {
  const resultsRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [fetchingClimate, setFetchingClimate] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState('idle');

  const [params, setParams] = useState({
    binder_content: 380,
    cement_type: 'CEM_I',
    addition_percent: 0,
    density: 2400,
    specific_heat: 1.0,
    thermal_conductivity: 1.8,
    pour_thickness: 1000,
    insulation_type: 'none',
    wind_speed: 4,
    insulation_removal_time: 0,
    placing_temperature: 25,
    ambient_min: 20,
    ambient_mean: 28,
    ambient_max: 36,
    placing_time: 6,
    ground_specific_heat: 1.2,
    ground_density: 2000,
    ground_thermal_conductivity: 1.1,
    ground_temperature: 25,
  });

  const handleChange = (name, value) => {
    setParams(prev => ({ ...prev, [name]: value }));
  };

  const handleCementTypeChange = (val) => {
    const ct = CEMENT_TYPES.find(c => c.value === val);
    setParams(prev => ({
      ...prev,
      cement_type: val,
      addition_percent: ct && ct.minAdd > 0 ? ct.minAdd : 0,
    }));
  };

  // Fetch climate data when city or month changes
  useEffect(() => {
    if (!selectedCity) return;
    const fetchClimate = async () => {
      setFetchingClimate(true);
      try {
        const year = new Date().getFullYear() - 1;
        const month = String(selectedMonth).padStart(2, '0');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(year, selectedMonth, 0).getDate();
        const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
        const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${selectedCity.lat}&longitude=${selectedCity.lon}&start_date=${startDate}&end_date=${endDate}&daily=temperature_2m_min,temperature_2m_max,temperature_2m_mean&timezone=auto`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.daily) {
          const mins = data.daily.temperature_2m_min.filter(v => v !== null);
          const maxs = data.daily.temperature_2m_max.filter(v => v !== null);
          const means = data.daily.temperature_2m_mean.filter(v => v !== null);
          if (mins.length && maxs.length && means.length) {
            const avgMin = Math.round(mins.reduce((a, b) => a + b, 0) / mins.length);
            const avgMax = Math.round(maxs.reduce((a, b) => a + b, 0) / maxs.length);
            const avgMean = Math.round(means.reduce((a, b) => a + b, 0) / means.length);
            setParams(prev => ({
              ...prev,
              ambient_min: avgMin,
              ambient_mean: avgMean,
              ambient_max: avgMax,
              ground_temperature: avgMean,
            }));
          }
        }
      } catch (e) { console.error('Climate fetch failed', e); }
      setFetchingClimate(false);
    };
    fetchClimate();
  }, [selectedCity, selectedMonth]);

  const handleCalculate = async () => {
    if (params.cement_type === 'OTHER') {
      setError('specialist');
      return;
    }
    if (!selectedCity) {
      setError('Please select a city to determine ambient temperatures.');
      return;
    }
    setLoading(true);
    setError('');
    setResults(null);
    setShowContactForm(false);
    try {
      const body = {
        ...params,
        city_name: selectedCity ? `${selectedCity.name}, ${selectedCity.country}` : null,
        city_latitude: selectedCity?.lat || null,
        city_longitude: selectedCity?.lon || null,
      };
      const res = await fetch(`${API_URL}/api/calculator/thermal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        if (res.status === 429) {
          throw new Error(
            errData?.detail ||
              "You've reached the daily lookup limit. Need more? Contact us at info@durabledesigns.co.uk and we'll lift the cap.",
          );
        }
        throw new Error(errData?.detail || 'Calculation failed');
      }
      const data = await res.json();
      setResults(data);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth' }), 200);
    } catch (e) {
      setError(e.message || 'Calculation failed. Please check inputs.');
    } finally {
      setLoading(false);
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    setContactStatus('sending');
    try {
      const res = await fetch(`${API_URL}/api/calculator/thermal/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      if (res.ok) setContactStatus('sent');
      else setContactStatus('error');
    } catch { setContactStatus('error'); }
  };

  const cementDef = CEMENT_TYPES.find(c => c.value === params.cement_type);
  const showAddition = cementDef && cementDef.maxAdd > 0;
  const exceedsThresholds = results && (results.warning_high_temp || results.warning_high_differential);
  const cw = results?.cold_weather;

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <SEO title="Mass Concrete Temperature Predictor | Durable Designs" description="Predict peak temperatures, thermal differentials and DEF risk in pile caps and mass foundations." />

      {/* Hero */}
      <section className="relative pt-24 pb-14 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ background: '#06120B' }}>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide mb-6" style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' }}>
            <Thermometer className="w-3.5 h-3.5" /> Mass Concrete Thermal Analysis
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tighter leading-[1.08] mb-4" data-testid="thermal-heading">
            Temperature Predictor for Pile Caps &amp; Mass Foundations
          </h1>
          <p className="text-base text-neutral-400 leading-relaxed max-w-2xl">
            Estimate peak temperatures and thermal differentials in mass concrete pours.
            Identify risks of delayed ettringite formation (DEF) and early-age thermal cracking
            before casting.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* --- Input Panel --- */}
            <div className="lg:col-span-2 space-y-6">

              {/* Location */}
              <Card className="border-gray-200">
                <CardContent className="pt-5 pb-5 space-y-4">
                  <h3 className="text-sm font-bold text-gray-800 tracking-tight flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-emerald-600" /> Location &amp; Season
                  </h3>
                  <CitySearch selected={selectedCity} onSelect={setSelectedCity} />
                  {selectedCity && (
                    <div>
                      <Label className="text-xs text-gray-500 mb-1.5 block">Casting month</Label>
                      <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
                      {fetchingClimate && <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading climate data...</p>}
                    </div>
                  )}
                  {!selectedCity && (
                    <p className="text-xs text-gray-400 italic">Select a city to determine ambient temperatures for your casting location.</p>
                  )}
                </CardContent>
              </Card>

              {/* Concrete Mix */}
              <Card className="border-gray-200">
                <CardContent className="pt-5 pb-5 space-y-4">
                  <h3 className="text-sm font-bold text-gray-800 tracking-tight">Concrete Mix</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Cement / binder type</Label>
                      <select
                        data-testid="cement-type-select"
                        value={params.cement_type}
                        onChange={e => handleCementTypeChange(e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        {CEMENT_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                      </select>
                    </div>
                    {showAddition && (
                      <div>
                        <Label className="text-xs text-gray-500">Addition ({cementDef.value === 'CEM_I_FA' ? 'fly ash' : 'GGBS'}) %</Label>
                        <Input data-testid="addition-percent" type="number" min={cementDef.minAdd} max={cementDef.maxAdd} value={params.addition_percent} onChange={e => handleChange('addition_percent', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-gray-500">Total binder content (kg/m&sup3;)</Label>
                      <Input data-testid="binder-content" type="number" min={200} max={600} value={params.binder_content} onChange={e => handleChange('binder_content', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Placing temperature (&deg;C)</Label>
                      <Input data-testid="placing-temperature" type="number" min={0} max={50} value={params.placing_temperature} onChange={e => handleChange('placing_temperature', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section */}
              <Card className="border-gray-200">
                <CardContent className="pt-5 pb-5 space-y-4">
                  <h3 className="text-sm font-bold text-gray-800 tracking-tight">Section Details</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Pour thickness (mm)</Label>
                      <Input data-testid="pour-thickness" type="number" min={300} max={5000} step={50} value={params.pour_thickness} onChange={e => handleChange('pour_thickness', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Top surface insulation</Label>
                      <select
                        data-testid="insulation-select"
                        value={params.insulation_type}
                        onChange={e => handleChange('insulation_type', e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        {INSULATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ambient (auto-filled from city — read-only) */}
              {selectedCity && (
                <Card className="border-gray-200">
                  <CardContent className="pt-5 pb-5 space-y-3">
                    <h3 className="text-sm font-bold text-gray-800 tracking-tight">
                      Ambient Temperature
                      <span className="font-normal text-gray-400 ml-2">(from climate data)</span>
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">Min</p>
                        <p className="text-lg font-bold text-gray-800">{params.ambient_min}&deg;C</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">Mean</p>
                        <p className="text-lg font-bold text-gray-800">{params.ambient_mean}&deg;C</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">Max</p>
                        <p className="text-lg font-bold text-gray-800">{params.ambient_max}&deg;C</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Advanced */}
              <button
                data-testid="toggle-advanced"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Advanced parameters
              </button>

              {showAdvanced && (
                <div className="space-y-4">
                  <Card className="border-gray-200">
                    <CardContent className="pt-5 pb-5 space-y-4">
                      <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider">Concrete Properties</h3>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-gray-500">Density (kg/m&sup3;)</Label>
                          <Input type="number" value={params.density} onChange={e => handleChange('density', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Specific heat (kJ/kg&deg;C)</Label>
                          <Input type="number" step={0.1} value={params.specific_heat} onChange={e => handleChange('specific_heat', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Thermal conductivity (W/m&deg;C)</Label>
                          <Input type="number" step={0.1} value={params.thermal_conductivity} onChange={e => handleChange('thermal_conductivity', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-gray-200">
                    <CardContent className="pt-5 pb-5 space-y-4">
                      <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider">Ground Properties</h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-gray-500">Ground temperature (&deg;C)</Label>
                          <Input type="number" value={params.ground_temperature} onChange={e => handleChange('ground_temperature', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Thermal conductivity (W/m&deg;C)</Label>
                          <Input type="number" step={0.1} value={params.ground_thermal_conductivity} onChange={e => handleChange('ground_thermal_conductivity', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Density (kg/m&sup3;)</Label>
                          <Input type="number" value={params.ground_density} onChange={e => handleChange('ground_density', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Specific heat (kJ/kg&deg;C)</Label>
                          <Input type="number" step={0.1} value={params.ground_specific_heat} onChange={e => handleChange('ground_specific_heat', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-gray-200">
                    <CardContent className="pt-5 pb-5 space-y-4">
                      <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider">Other</h3>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-gray-500">Wind speed (m/s)</Label>
                          <Input type="number" value={params.wind_speed} onChange={e => handleChange('wind_speed', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Placing time (24h clock)</Label>
                          <Input type="number" min={0} max={24} value={params.placing_time} onChange={e => handleChange('placing_time', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Insulation removal (hours)</Label>
                          <Input type="number" min={0} value={params.insulation_removal_time} onChange={e => handleChange('insulation_removal_time', parseFloat(e.target.value) || 0)} className="mt-1 border-gray-200 text-sm" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Calculate Button */}
              <Button
                data-testid="calculate-btn"
                onClick={handleCalculate}
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 text-sm font-semibold"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculating...</> : 'Calculate Temperature'}
              </Button>

              {error === 'specialist' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-5" data-testid="specialist-notice">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Specialist Cement Type</p>
                      <p className="text-sm text-amber-700 mt-1">
                        For CEM II/A-L + Fly Ash, CEM VI, CEM II/C-M (LC3 calcined clay), and other
                        non-standard binder systems, the thermal behaviour requires specialist calibration.
                      </p>
                      <Link to="/engineering/contact" className="inline-flex items-center gap-1 text-sm font-semibold text-amber-800 mt-3 hover:underline">
                        Contact our team for advice <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              {error && error !== 'specialist' && (
                <p className="text-sm text-red-600 text-center" data-testid="error-message">{error}</p>
              )}
            </div>

            {/* --- Info Sidebar --- */}
            <div className="space-y-4">
              <Card className="border-gray-200 bg-gray-50">
                <CardContent className="pt-5 pb-5">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">Why does this matter?</h3>
                  <div className="space-y-3 text-xs text-gray-600 leading-relaxed">
                    <p><strong className="text-gray-700">DEF Risk:</strong> When peak concrete temperature exceeds ~70&deg;C, delayed ettringite formation can cause expansion and cracking years later.</p>
                    <p><strong className="text-gray-700">Thermal Cracking:</strong> Large temperature differentials (&gt;25&deg;C) between the core and surface cause restraint stresses that can exceed the tensile strength of young concrete.</p>
                    <p><strong className="text-gray-700">Hot Climates:</strong> High ambient and placing temperatures significantly increase peak temperatures in mass pours. Mitigation strategies include ice cooling, SCM replacement, and insulation management.</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-gray-200 bg-gray-50">
                <CardContent className="pt-5 pb-5">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">Applicable to</h3>
                  <ul className="space-y-1.5 text-xs text-gray-600">
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-emerald-500" /> Pile caps</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-emerald-500" /> Mass foundations &amp; raft slabs</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-emerald-500" /> Ground-bearing slabs &gt;300mm</li>
                    <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-emerald-500" /> Transfer slabs</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* ─── Results ─── */}
          {results && (
            <div ref={resultsRef} className="mt-12 space-y-6" data-testid="results-section">
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">Results</h2>

              {/* Key metrics */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  testId="metric-max-temp"
                  label="Peak Temperature"
                  value={`${results.max_temperature}°C`}
                  sub={`at ${results.max_temperature_time}h`}
                  warn={results.warning_high_temp}
                  warnText="Exceeds 70°C — DEF risk"
                />
                <MetricCard
                  testId="metric-diff-top"
                  label="Max Differential (top)"
                  value={`${results.max_differential_top}°C`}
                  sub={`at ${results.max_differential_top_time}h`}
                  warn={results.max_differential_top > 25}
                  warnText="Exceeds 25°C — cracking risk"
                />
                <MetricCard
                  testId="metric-diff-bottom"
                  label="Max Differential (bottom)"
                  value={`${results.max_differential_bottom}°C`}
                  sub={`at ${results.max_differential_bottom_time}h`}
                  warn={results.max_differential_bottom > 25}
                  warnText="Exceeds 25°C — cracking risk"
                />
                <MetricCard
                  testId="metric-t1"
                  label="Temperature Drop T1"
                  value={`${results.temperature_drop_t1}°C`}
                  sub="Peak − ambient mean"
                />
              </div>

              {/* Cold Weather Assessment — only shown when backend returns it */}
              {cw && (
                <div
                  data-testid="cold-weather-assessment"
                  className={`rounded-lg border p-5 ${
                    cw.status === 'REJECT' ? 'border-red-300 bg-red-50' :
                    cw.status === 'WARNING' ? 'border-amber-300 bg-amber-50' :
                    cw.status === 'WATCHOUT' ? 'border-amber-200 bg-amber-50/60' :
                    cw.status === 'CAUTION' ? 'border-orange-200 bg-orange-50' :
                    'border-emerald-200 bg-emerald-50'
                  }`}
                >
                  <div className="flex gap-3">
                    <div className={`shrink-0 mt-0.5 ${
                      cw.status === 'REJECT' || cw.status === 'WARNING' ? 'text-red-600' :
                      cw.status === 'WATCHOUT' ? 'text-amber-600' :
                      cw.status === 'CAUTION' ? 'text-orange-600' : 'text-emerald-600'
                    }`}>
                      {cw.status === 'PASS' ? (
                        <Shield className="w-5 h-5" />
                      ) : (
                        <AlertTriangle className="w-5 h-5" />
                      )}
                    </div>
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded ${
                          cw.status === 'REJECT' ? 'bg-red-600 text-white' :
                          cw.status === 'WARNING' ? 'bg-amber-600 text-white' :
                          cw.status === 'WATCHOUT' ? 'bg-amber-500 text-white' :
                          cw.status === 'CAUTION' ? 'bg-orange-500 text-white' :
                          'bg-emerald-600 text-white'
                        }`}>{cw.status}</span>
                        <span className="text-sm font-semibold text-gray-800">
                          {cw.applies ? 'Cold Weather Concreting Assessment' : 'Delivery Temperature Check'}
                          {cw.applies && <span className="text-xs font-normal text-gray-500 ml-1">(ACI 306R / BS EN 206)</span>}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{cw.message}</p>

                      {/* Full metrics row — only for cold weather (applies=true) */}
                      {cw.applies && (
                        <>
                          <div className="grid sm:grid-cols-3 gap-2 pt-1">
                            <div className="bg-white/60 rounded px-3 py-2 text-center">
                              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Section</p>
                              <p className="text-sm font-bold text-gray-800 capitalize">{cw.category}</p>
                            </div>
                            <div className="bg-white/60 rounded px-3 py-2 text-center">
                              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Min Curing Temp</p>
                              <p className="text-sm font-bold text-gray-800">{cw.t_min_cure}&deg;C</p>
                            </div>
                            <div className="bg-white/60 rounded px-3 py-2 text-center">
                              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Min Delivery Temp</p>
                              <p className="text-sm font-bold text-gray-800">{cw.t_delivery_min}&deg;C</p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed pt-1">{cw.summary}</p>
                          <div className="border-t border-gray-200/60 pt-2 mt-1">
                            <p className="text-xs text-gray-500 leading-relaxed">
                              <strong className="text-gray-700">Protection:</strong> {cw.protection_notes}
                            </p>
                          </div>
                        </>
                      )}

                      {/* Specialist CTA for WARNING / REJECT / WATCHOUT */}
                      {(cw.status === 'WARNING' || cw.status === 'REJECT' || cw.status === 'WATCHOUT') && (
                        <div className="pt-1">
                          {!showContactForm ? (
                            <button
                              data-testid="cold-weather-contact-btn"
                              onClick={() => setShowContactForm(true)}
                              className={`inline-flex items-center gap-1 text-sm font-semibold hover:underline ${
                                cw.status === 'WATCHOUT' ? 'text-amber-800' : 'text-red-800'
                              }`}
                            >
                              Speak to a specialist <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Warning banner */}
              {exceedsThresholds && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-5" data-testid="threshold-warning">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-800">Specialist Verification Advised</p>
                      <p className="text-sm text-red-700 mt-1">
                        {results.warning_high_temp && 'The predicted peak temperature exceeds 70°C, indicating potential risk of delayed ettringite formation (DEF). '}
                        {results.warning_high_differential && 'The predicted temperature differential exceeds 25°C, indicating potential risk of early-age thermal cracking. '}
                        We recommend verification by one of our specialists.
                      </p>
                      {!showContactForm && (
                        <button
                          data-testid="request-specialist-btn"
                          onClick={() => setShowContactForm(true)}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-red-800 mt-3 hover:underline"
                        >
                          Request specialist review <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Contact form (conditional) */}
              {showContactForm && contactStatus !== 'sent' && (
                <Card className="border-emerald-200 bg-emerald-50/50">
                  <CardContent className="pt-5 pb-5">
                    <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <Send className="w-4 h-4 text-emerald-600" /> Request Specialist Review
                    </h3>
                    <form onSubmit={handleContact} className="space-y-3">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Input data-testid="contact-name" required placeholder="Your name" value={contactForm.name} onChange={e => setContactForm(prev => ({ ...prev, name: e.target.value }))} className="border-gray-200 text-sm" />
                        <Input data-testid="contact-email" required type="email" placeholder="Email address" value={contactForm.email} onChange={e => setContactForm(prev => ({ ...prev, email: e.target.value }))} className="border-gray-200 text-sm" />
                      </div>
                      <textarea data-testid="contact-message" rows={3} placeholder="Any additional details (optional)" value={contactForm.message} onChange={e => setContactForm(prev => ({ ...prev, message: e.target.value }))} className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                      <div className="flex justify-end">
                        <Button type="submit" disabled={contactStatus === 'sending'} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-5" data-testid="contact-submit">
                          {contactStatus === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Request'}
                        </Button>
                      </div>
                      {contactStatus === 'error' && <p className="text-xs text-red-500">Failed to send. Please try again.</p>}
                    </form>
                  </CardContent>
                </Card>
              )}
              {contactStatus === 'sent' && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="contact-success">
                  <p className="text-sm font-medium text-emerald-800">Thank you — one of our specialists will be in touch shortly.</p>
                </div>
              )}

              {/* Charts */}
              {results.time_series && results.time_series.length > 0 && (
                <Card className="border-gray-200">
                  <CardContent className="pt-5 pb-5">
                    <h3 className="text-sm font-bold text-gray-800 mb-4">Temperature History</h3>
                    <div className="h-[320px]" data-testid="temp-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={results.time_series} margin={{ top: 5, right: 20, bottom: 20, left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="time" label={{ value: 'Time (hours)', position: 'insideBottom', offset: -10, style: { fontSize: 11, fill: '#888' } }} tick={{ fontSize: 10 }} />
                          <YAxis label={{ value: '°C', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#888' } }} tick={{ fontSize: 10 }} />
                          <Tooltip contentStyle={{ fontSize: 11 }} />
                          <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="6 3" label={{ value: '70°C DEF', fill: '#ef4444', fontSize: 10, position: 'right' }} />
                          <Line type="monotone" dataKey="max_temp" stroke="#059669" strokeWidth={2} name="Peak in section" dot={false} />
                          <Line type="monotone" dataKey="top_surface" stroke="#3b82f6" strokeWidth={1.5} name="Top surface" dot={false} />
                          <Line type="monotone" dataKey="bottom_slab" stroke="#8b5cf6" strokeWidth={1.5} name="Bottom of slab" dot={false} />
                          <Line type="monotone" dataKey="ambient" stroke="#9ca3af" strokeWidth={1} strokeDasharray="4 2" name="Ambient" dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {results.time_series && results.time_series.length > 0 && (
                <Card className="border-gray-200">
                  <CardContent className="pt-5 pb-5">
                    <h3 className="text-sm font-bold text-gray-800 mb-4">Temperature Differentials</h3>
                    <div className="h-[280px]" data-testid="diff-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={results.time_series} margin={{ top: 5, right: 20, bottom: 20, left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="time" label={{ value: 'Time (hours)', position: 'insideBottom', offset: -10, style: { fontSize: 11, fill: '#888' } }} tick={{ fontSize: 10 }} />
                          <YAxis label={{ value: '°C', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#888' } }} tick={{ fontSize: 10 }} />
                          <Tooltip contentStyle={{ fontSize: 11 }} />
                          <ReferenceLine y={25} stroke="#ef4444" strokeDasharray="6 3" label={{ value: '25°C limit', fill: '#ef4444', fontSize: 10, position: 'right' }} />
                          <Area type="monotone" dataKey="differential_top" stroke="#f59e0b" fill="#fef3c7" strokeWidth={2} name="Top differential" />
                          <Area type="monotone" dataKey="differential_bottom" stroke="#8b5cf6" fill="#ede9fe" strokeWidth={1.5} name="Bottom differential" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Depth profile at peak temp */}
              {results.depth_profile_at_max_temp && results.depth_profile_at_max_temp.length > 0 && (
                <Card className="border-gray-200">
                  <CardContent className="pt-5 pb-5">
                    <h3 className="text-sm font-bold text-gray-800 mb-4">Temperature Profile at Peak ({results.max_temperature_time}h)</h3>
                    <div className="h-[280px]" data-testid="profile-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={results.depth_profile_at_max_temp} margin={{ top: 5, right: 20, bottom: 20, left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="depth_mm" label={{ value: 'Depth from bottom (mm)', position: 'insideBottom', offset: -10, style: { fontSize: 11, fill: '#888' } }} tick={{ fontSize: 10 }} />
                          <YAxis label={{ value: '°C', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#888' } }} tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                          <Tooltip contentStyle={{ fontSize: 11 }} />
                          <Line type="monotone" dataKey="temperature" stroke="#059669" strokeWidth={2} dot={{ r: 2 }} name="Temperature" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Full Thermal Control Plan CTA — always after results */}
          {results && (
            <div data-testid="full-analysis-cta" className="mt-8 rounded-lg border border-gray-200 bg-gray-50 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-gray-800 tracking-tight">Need a full thermal control plan?</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mt-1">
                    2D or 3D heat of hydration simulation, thermal cracking assessment,
                    and active cooling/heating design — tailored to your project.
                  </p>
                </div>
                <button
                  data-testid="request-full-analysis-btn"
                  onClick={() => setShowContactForm(true)}
                  className="shrink-0 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
                >
                  Request full analysis <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-12 border-t border-gray-100 pt-8">
            <div className="flex gap-3 text-xs text-gray-400 leading-relaxed max-w-3xl">
              <Shield className="w-4 h-4 shrink-0 mt-0.5 text-gray-300" />
              <p>
                <strong className="text-gray-500">Disclaimer:</strong> This tool provides indicative estimates
                for preliminary assessment purposes only. Results are based on simplified 1D finite difference
                modelling with empirical calibration data and should not be relied upon for detailed design or
                construction decisions. Durable Designs accepts no liability for any loss, damage, or
                consequence arising from the use of this tool. For project-specific thermal analysis, independent
                specialist verification is essential. All calculations are subject to the accuracy of user-provided
                inputs and the limitations of the underlying model assumptions.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ─── Metric Card ──────────────────────────────────────────────────────
function MetricCard({ testId, label, value, sub, warn, warnText }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-lg border p-4 ${warn ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
    >
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold tracking-tight ${warn ? 'text-red-700' : 'text-gray-900'}`}>{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
      {warn && warnText && (
        <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {warnText}
        </p>
      )}
    </div>
  );
}
